import React, { useEffect, useState } from "react";
import { Flame, X, MapPin, ShieldCheck } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import BottomNav from "../components/BottomNav";
import Logo from "../components/Logo";
import { toast } from "sonner";

export default function Grid() {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [maxDistance, setMaxDistance] = useState(100);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/discover?max_distance=${maxDistance}`);
            setProfiles(data);
        } catch {
            toast.error("Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [maxDistance]);

    const swipe = async (target_user_id, action) => {
        setProfiles((p) => p.filter((u) => u.user_id !== target_user_id));
        try {
            const { data } = await api.post("/swipe", { target_user_id, action });
            if (data.matched) toast.success(`It's a smash with ${data.target.name}!`);
        } catch {}
    };

    return (
        <div className="min-h-screen pb-20">
            <header className="px-5 pt-5 pb-3 flex items-center justify-between sticky top-0 bg-ink-950/90 backdrop-blur-xl z-40 border-b border-white/5">
                <Logo size="sm" />
                <h1 className="font-display text-xl text-white">THE LINEUP</h1>
            </header>

            <div className="px-5 py-3 flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-widest text-white/50">Range</span>
                <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={maxDistance}
                    onChange={(e) => setMaxDistance(Number(e.target.value))}
                    data-testid="grid-range-slider"
                    className="flex-1 accent-smash"
                />
                <span className="text-xs font-display text-dash w-12 text-right">{maxDistance}km</span>
            </div>

            {loading ? (
                <div className="p-10 text-center text-white/50 text-xs uppercase tracking-widest">Loading…</div>
            ) : profiles.length === 0 ? (
                <div className="p-10 text-center" data-testid="grid-empty">
                    <Flame className="w-8 h-8 text-smash mx-auto mb-2" />
                    <p className="text-sm text-white/60">No one in range. Widen the slider above.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-1 px-1" data-testid="grid-list">
                    {profiles.map((p) => (
                        <div
                            key={p.user_id}
                            className="relative aspect-[3/4] bg-ink-800 overflow-hidden group"
                            data-testid={`grid-card-${p.user_id}`}
                        >
                            {p.photos?.[0] ? (
                                <img src={fileUrl(p.photos[0])} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center font-display text-5xl text-white/20">
                                    {p.name?.[0] || "?"}
                                </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                                <div className="flex items-center gap-1">
                                    <span className="font-display text-base leading-none truncate">{p.name?.toUpperCase()}</span>
                                    <span className="font-display text-sm text-white/60">{p.age}</span>
                                    {p.verified && <ShieldCheck className="w-3 h-3 text-dash" />}
                                </div>
                                {p.distance_km != null && (
                                    <div className="text-[10px] text-white/60 flex items-center gap-1">
                                        <MapPin className="w-2.5 h-2.5" /> {p.distance_km}km
                                    </div>
                                )}
                            </div>
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => swipe(p.user_id, "pass")}
                                    data-testid={`grid-pass-${p.user_id}`}
                                    className="w-8 h-8 bg-black/70 border border-white/20 text-white flex items-center justify-center"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => swipe(p.user_id, "like")}
                                    data-testid={`grid-smash-${p.user_id}`}
                                    className="w-8 h-8 bg-smash text-white flex items-center justify-center"
                                >
                                    <Flame className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <BottomNav />
        </div>
    );
}
